import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { AuthLayout } from './AuthLayout';
import { useAuth } from './useAuth';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { authApi } from '@/lib/authApi';

type Status = 'verificando' | 'ok' | 'falhou' | 'aguardando';

/**
 * Consome o token do link de verificação (RF-01.02).
 *
 * Sem token na URL, a tela vira o lugar de reenviar — que é onde cai quem
 * entrou antes de confirmar o e-mail. O reenvio confirma igual em qualquer
 * situação, inclusive acima do limite de 3/h, porque a resposta do servidor é
 * deliberadamente indistinguível.
 */
export function VerifyEmailPage(): JSX.Element {
  const [params] = useSearchParams();
  const { session } = useAuth();
  const token = params.get('token');

  const [status, setStatus] = useState<Status>(token === null ? 'aguardando' : 'verificando');
  const [resent, setResent] = useState(false);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    // Verifica presença do token na URL, não igualdade com segredo.
    // eslint-disable-next-line security/detect-possible-timing-attacks
    if (token === null) {
      return undefined;
    }

    let cancelled = false;

    void (async () => {
      try {
        await authApi.verifyEmail(token);

        if (!cancelled) {
          setStatus('ok');
        }
      } catch {
        if (!cancelled) {
          setStatus('falhou');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  async function resend(): Promise<void> {
    if (session === null) {
      return;
    }

    setIsResending(true);

    try {
      await authApi.resendVerification(session.user.email);
    } finally {
      setResent(true);
      setIsResending(false);
    }
  }

  return (
    <AuthLayout footer={<Link to="/entrar">Ir para o acesso</Link>} title="Confirmação de e-mail">
      {status === 'verificando' && <Alert tone="info">Confirmando seu e-mail…</Alert>}

      {status === 'ok' && (
        <Alert title="E-mail confirmado" tone="success">
          Sua conta está ativa. Você já pode entrar.
        </Alert>
      )}

      {status === 'falhou' && (
        <Alert title="Link inválido ou expirado" tone="error">
          Este link não vale mais. Peça um novo pela tela de acesso.
        </Alert>
      )}

      {status === 'aguardando' && (
        <>
          <Alert title="Falta confirmar seu e-mail" tone="warning">
            Enviamos um link de confirmação. Enquanto ele não for usado, seu acesso fica limitado ao
            perfil.
          </Alert>

          {session !== null && !resent && (
            <Button loading={isResending} onClick={() => void resend()} variant="secondary">
              Reenviar confirmação
            </Button>
          )}

          {resent && (
            <Alert tone="info">
              Se ainda houver confirmação pendente, um novo link foi enviado.
            </Alert>
          )}
        </>
      )}
    </AuthLayout>
  );
}
